import { ReactNode, HTMLAttributes } from "react";
import { cn } from "@/lib/utils";

/**
 * RoavaCard — canonical card surface.
 *
 * Standard: 24px radius, soft beige border, white/cream bg, 24px padding.
 * Optional icon (48px container), title, description, footer slots.
 */
export interface RoavaCardProps extends Omit<HTMLAttributes<HTMLDivElement>, "title"> {
  icon?: ReactNode;
  iconTone?: "blue" | "green" | "orange" | "red" | "gray";
  title?: ReactNode;
  description?: ReactNode;
  footer?: ReactNode;
  /** Removes default padding; use when children fully control their own layout. */
  flush?: boolean;
}

const toneBg: Record<NonNullable<RoavaCardProps["iconTone"]>, string> = {
  blue: "bg-[var(--roava-blue)]/10 text-[var(--roava-blue)]",
  green: "bg-[var(--roava-green)]/10 text-[var(--roava-green)]",
  orange: "bg-[var(--roava-orange)]/15 text-[var(--roava-orange)]",
  red: "bg-[var(--roava-red)]/10 text-[var(--roava-red)]",
  gray: "bg-[var(--roava-gray)] text-[var(--roava-text-muted)]",
};

export function RoavaCard({
  icon,
  iconTone = "blue",
  title,
  description,
  footer,
  flush = false,
  className,
  children,
  ...rest
}: RoavaCardProps) {
  const hasHeader = !!(icon || title || description);
  return (
    <div
      {...rest}
      className={cn(
        "rounded-[24px] border bg-[var(--roava-card)] text-[var(--roava-text)]",
        "border-[var(--roava-border)]",
        "min-w-0 w-full max-w-full overflow-hidden",
        flush ? "" : "p-6",
        className,
      )}
    >
      {hasHeader && (
        <div className="flex items-start gap-4">
          {icon && (
            <div
              className={cn(
                "size-12 shrink-0 rounded-2xl grid place-items-center",
                toneBg[iconTone],
              )}
            >
              {icon}
            </div>
          )}
          <div className="min-w-0 flex-1">
            {title && <div className="text-base font-semibold leading-tight">{title}</div>}
            {description && (
              <p className="mt-1 text-sm text-[var(--roava-text-muted)]">{description}</p>
            )}
          </div>
        </div>
      )}
      {children != null && (
        <div className={cn(hasHeader ? "mt-4" : "")}>{children}</div>
      )}
      {footer && <div className="mt-4">{footer}</div>}
    </div>
  );
}
